import { z } from "zod";
import { sequenceSlotSchema } from "./call-sequence.js";
import { ANSWERED_OUTCOMES, CALL_END_REASONS, CALL_LIVE_STATES, CALL_RECORDING_STATES, CALL_SOURCES, callQualityViewSchema } from "./calls.js";
import { freeTextSchema } from "./leads.js";
import { emailEventSchema } from "./email-events.js";
import { actorViewSchema } from "./people.js";
import { whatsAppAutomaticMessageSchema, whatsAppChannelSchema, whatsAppContentKindSchema, whatsAppMessageStatusSchema, whatsAppTemplateKeySchema } from "./whatsapp.js";
// Timeline del Lead (piano M2, Task 8; D3): telefonate, note e registro delle attività del Lead in un'unica lista,
// dalla più recente, a pagine con un cursore. Una telefonata porta con sé i suoi appunti; le modifiche di un
// salvataggio sono un elemento solo, con il dettaglio di ogni campo. Le date sono stringhe ISO: l'interfaccia le
// scrive nel fuso dell'azienda. Le etichette dei campi di sistema sono chiavi dei cataloghi (`leadCard.fields.*`),
// quelle dei campi dell'azienda il nome che il campo aveva al momento della modifica.
// «Tutto», «Telefonate», «Note», «Modifiche», «Email» (dall'ingresso dei Lead, I5, D5: gli eventi delle email di
// GoHighLevel) e «Storico di Close». «WhatsApp», «Task» e «Appuntamenti e opportunità» del mock-up non ci sono ancora.
export const TIMELINE_FILTERS = ["all", "calls", "notes", "changes", "email", "history"];
/** La lunghezza massima del cursore: il server non ne emette mai uno più lungo. */
export const TIMELINE_CURSOR_MAX_LENGTH = 200;
/**
 * Query di `GET /api/leads/:leadId/timeline`: un parametro sconosciuto, o un valore fuori dai limiti, è `422
 * validation_failed`; un cursore che non viene da una pagina precedente anche. La ricerca segue la regola dei testi
 * liberi di `text.ts`, come quella dell'elenco dei Lead: tab e a capo separano le parole, un altro carattere di
 * controllo è `422`. Il cursore resta una stringa qualunque, come un token: il server lo decodifica e ne controlla
 * ogni parte prima della query.
 */
export const timelineQuerySchema = z.strictObject({
    filter: z.enum(TIMELINE_FILTERS).default("all"),
    q: freeTextSchema(200).optional(),
    cursor: z.string().max(TIMELINE_CURSOR_MAX_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
// `fromClose` segue la provenienza, mai il tipo di attore: un autore di Close abbinato a una persona del team è un
// attore `membership`, ma ciò che ha fatto in Close resta «da Close».
const base = { id: z.string(), occurredAt: z.string(), occurredAtKnown: z.boolean(), actor: actorViewSchema, fromClose: z.boolean() };
/**
 * La registrazione di una telefonata come la vede la Timeline (piano M4, Task 12): lo stato da mostrare (`recordingDisplayState`
 * del dominio), la durata dell'audio e quando l'audio scadrà, se l'azienda ha una durata (oggi vuoto, questioni 20 e 166).
 */
export const timelineRecordingSchema = z.object({
    state: z.enum(CALL_RECORDING_STATES),
    durationSeconds: z.number().int().nullable(),
    expiresAt: z.string().nullable(),
});
/**
 * Un allegato di una nota di Close (INGR-27: gli allegati come l'audio, domanda n. 82), come lo mostra la Timeline: il nome, il tipo e
 * la misura (quelli arrivati, o dichiarati da Close finché non è copiato) e lo stato della copia: `copied` si apre con
 * `GET /api/note-attachments/:attachmentId/link`; `to_copy` aspetta la copia; `failed` non è riuscita; `missing` Close non l'ha più; `expired` sta per essere tolto con il suo Lead («Elimina per sempre»).
 * `opensInline`: si apre nel browser (immagini e PDF, `noteAttachmentOpensInline`); gli altri si scaricano.
 */
export const timelineNoteAttachmentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    contentType: z.string().nullable(),
    sizeBytes: z.number().int().nonnegative().nullable(),
    state: z.enum(["to_copy", "copied", "missing", "failed", "expired"]),
    opensInline: z.boolean(),
});
/**
 * I tipi di file che si aprono nel browser invece di scaricarsi: immagini comuni e PDF, che il browser mostra senza eseguire nulla.
 * Ogni altro tipo (HTML, SVG, script, documenti da ufficio, archivi, sconosciuti) si scarica (`Content-Disposition: attachment`).
 */
export const NOTE_ATTACHMENT_INLINE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
export function noteAttachmentOpensInline(contentType) {
    const type = (contentType?.split(";")[0] ?? "").trim().toLowerCase();
    return NOTE_ATTACHMENT_INLINE_TYPES.includes(type);
}
/** `GET /api/note-attachments/:attachmentId/link`: l'indirizzo di Gioia365 per aprire l'allegato, valido cinque minuti. */
export const noteAttachmentLinkResponseSchema = z.object({ url: z.string(), expiresAt: z.string() });
export const timelineItemSchema = z.discriminatedUnion("kind", [
    // Gli appunti della telefonata, dal più vecchio: non compaiono mai anche come note a sé. `sequenceSlot` (piano M5, Task 11) è
    // il posto della telefonata nella sequenza di chiamate, «tentativo N · chiamata M»: `null` senza un posto, per una chiamata
    // non arrivata al Lead e in una risposta di prima del 4 ottobre 2026.
    // I fatti della telefonia (piano M4, Task 12), tutti facoltativi in lettura perché una risposta di prima non li porta:
    // - `source`, da dove viene la telefonata;
    // - `liveState`, che cosa ne vede chi guarda (`ended` per quelle di Close e a mano): finché non è `ended` la chiamata è
    //   «In corso», e la sua validità, l'esito e la durata non sono ancora quelli della fine;
    // - `answeredOutcome`, il secondo passo dell'esito dopo «Risposta»; `endedReason`, come è finita (`null` finché è in corso
    //   e per quelle di Close e a mano);
    // - `recording`, lo stato della registrazione, `null` quando non c'è niente da registrare;
    // - `canListen`, se chi guarda può ascoltarla: l'autore, oppure chi ha «Ascoltare le registrazioni dei colleghi», mai il
    //   contesto di piattaforma; nella «Guarda come…» la persona entrata, come `GET /api/calls/:callId/recording`.
    z.object({
        kind: z.literal("call"),
        ...base,
        direction: z.enum(["outbound", "inbound"]),
        outcome: z.string().nullable(),
        outcomeDetail: z.string().nullable(),
        durationSeconds: z.number().int().nullable(),
        valid: z.boolean(),
        notes: z.array(z.object({ id: z.string(), body: z.string(), occurredAt: z.string(), actor: actorViewSchema })),
        sequenceSlot: sequenceSlotSchema.nullable().default(null),
        source: z.enum(CALL_SOURCES).optional(),
        liveState: z.enum(CALL_LIVE_STATES).optional(),
        answeredOutcome: z.enum(ANSWERED_OUTCOMES).nullable().optional(),
        endedReason: z.enum(CALL_END_REASONS).nullable().optional(),
        quality: callQualityViewSchema.nullable().optional(),
        recording: timelineRecordingSchema.nullable().optional(),
        canListen: z.boolean().optional(),
    }),
    // `occurredAtKnown` è falso per una nota di Close senza data («data originale non nota»). `attachments`, soltanto quando ce ne
    // sono, gli allegati della nota di Close (INGR-27) con lo stato della copia.
    z.object({ kind: z.literal("note"), ...base, body: z.string(), originLabel: z.string().nullable(), attachments: z.array(timelineNoteAttachmentSchema).optional() }),
    // Un salvataggio: `id` è la sua correlazione.
    z.object({
        kind: z.literal("change"),
        ...base,
        correlationId: z.string(),
        changes: z.array(z.object({ type: z.string(), fieldRef: z.string().nullable(), fieldLabel: z.string().nullable(), before: z.unknown(), after: z.unknown(), reason: z.string().nullable() })),
    }),
    // Ogni altro evento del registro: creazione, importazione, storico di Close, archiviazione e ripristino (piano M3, Task
    // 11), gli eventi della sequenza di chiamate (piano M5, D18: `sequence.started`, `sequence.restarted`,
    // `sequence.attempt_closed`, `sequence.violated`, `sequence.ended` con `endReason` nel payload). `reason` è il motivo scritto
    // con l'evento (quello dell'archiviazione), `null` se non c'è; una risposta di prima del 2 ottobre 2026 non lo porta.
    // `before` e `after` soltanto per gli eventi che la Timeline scrive con il prima e il dopo (`TIMELINE_EVENTS_WITH_VALUES`:
    // «Sposta» di un richiamo, piano M5, Task 17); per gli altri mancano.
    z.object({
        kind: z.literal("event"),
        ...base,
        type: z.string(),
        payload: z.record(z.string(), z.unknown()),
        reason: z.string().nullable().optional(),
        before: z.record(z.string(), z.unknown()).nullable().optional(),
        after: z.record(z.string(), z.unknown()).nullable().optional(),
    }),
    // Un WhatsApp (piano M6, R1, Task 3b; D3, questione 402): un elemento per messaggio, con lo stato di adesso, invece delle due
    // righe del mock-up; gli eventi `whatsapp.*` del registro non compaiono anche come eventi a sé. `id` è il messaggio, `occurredAt`
    // l'ora della preparazione (o dell'arrivo), `statusAt` quella dello stato di adesso («dichiarato inviato»); `actor` è chi l'ha
    // preparato, come lo ha scritto l'evento `whatsapp.prepared`. Il testo è quello esatto che il Lead ha ricevuto, con la riga «scrivi
    // STOP»; `templateName` è il nome di adesso del modello da cui nasce. `canDeclareSent`: chi guarda è l'autore e il messaggio è
    // ancora soltanto preparato, quindi può dire «Sì, inviato». Dal WhatsApp ufficiale (piano M6, R2, Task 10a) lo stato è quello
    // che dice Meta e `actor` è chi l'ha mandato, come lo ha scritto l'evento `whatsapp.sent` o `whatsapp.failed`; un messaggio `failed`
    // porta il codice del fornitore (`<code>` o `<code>:<subcode>` di Meta, `not_confirmed` per «Non confermato», questione 415) e il
    // titolo del fornitore come motivo, nella sua lingua («Non consegnato» con il motivo: i codici più comuni hanno un testo nei
    // cataloghi); nulli per gli altri stati. Un messaggio del Lead (piano M6, R3, passo 11a; `direction: "in"`, stato `received`) è
    // all'ora del fornitore, con `toE164` il numero dell'azienda che l'ha ricevuto e `fromE164` quello del Lead (vuoto per chi scrive con
    // il solo nome utente); `contentKind` dice che cosa ha mandato (`body` è il testo, o la didascalia: questione 425), vuoto per un
    // messaggio in uscita; `readAt` quando è stato letto in Gioia365, vuoto finché è «Da leggere», e `readBy` da chi (il nome di adesso
    // della persona: «Letto da …», passo 11c, questione 423; vuoto con `readAt` per una lettura del contesto di Gioia365, «Letto da
    // Gioia365 il …»); `canMarkRead`: chi guarda lo segna letto ora (è da leggere e sta nel suo perimetro di «Conversazioni», nella
    // «Guarda come…» quello della persona osservata: la regola di `POST /api/whatsapp/messages/:messageId/read`). `automatic` dice un
    // messaggio che Gioia365 manda da sé, senza autore (passo 11b, questione 420: la conferma dello «STOP», nella Timeline «Gioia365
    // (automatico)»).
    z.object({
        kind: z.literal("whatsapp"),
        ...base,
        direction: z.enum(["out", "in"]),
        channel: whatsAppChannelSchema,
        status: whatsAppMessageStatusSchema,
        statusAt: z.string(),
        body: z.string(),
        templateKey: whatsAppTemplateKeySchema.nullable(),
        templateName: z.string().nullable(),
        toE164: z.string(),
        fromE164: z.string().nullable(),
        callId: z.string().nullable(),
        canDeclareSent: z.boolean(),
        failureCode: z.string().nullable(),
        failureReason: z.string().nullable(),
        contentKind: whatsAppContentKindSchema.nullable(),
        readAt: z.string().nullable(),
        readBy: z.string().nullable(),
        canMarkRead: z.boolean(),
        automatic: whatsAppAutomaticMessageSchema.nullable(),
    }),
    // Un evento delle email di GoHighLevel (piano dell'ingresso del 12 ottobre 2026, I5, D5; INGR-22): un elemento per evento, in sola
    // lettura, all'ora dell'evento (quella che dice GoHighLevel, altrimenti quella dell'arrivo); `actor` è GoHighLevel (un attore di
    // sistema); l'oggetto e il link, se il flusso li manda (il link è sempre `http` o `https`). Il filtro «Tutto» e il filtro «Email» li
    // mostrano.
    z.object({
        kind: z.literal("email"),
        ...base,
        event: emailEventSchema,
        subject: z.string().nullable(),
        url: z.string().nullable(),
    }),
]);
/**
 * Gli eventi che la Timeline mostra con il prima e il dopo del registro (`before`/`after` dell'evento): soltanto quelli che
 * li scrivono per sé, così nessun altro valore del registro esce dalla Timeline senza una ragione.
 */
export const TIMELINE_EVENTS_WITH_VALUES = ["task.rescheduled"];
/** Una pagina; `nextCursor` nullo quando non ce ne sono altre. */
export const timelinePageSchema = z.object({ items: z.array(timelineItemSchema), nextCursor: z.string().nullable() });
//# sourceMappingURL=timeline.js.map